import './party.css';

import { uiText } from './text.js';

export interface PlayerMenuEntry {
  label: string;
  run: () => void;
}

/**
 * The little menu a click on another player opens, where the click was. Any
 * other click or Escape closes it; picking an entry runs it and closes it.
 */
export class PlayerMenu {
  private root?: HTMLElement;
  private readonly onKey = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') this.close();
  };
  private readonly onOutside = (event: PointerEvent): void => {
    if (this.root !== undefined && !this.root.contains(event.target as Node)) this.close();
  };

  open(title: string, entries: readonly PlayerMenuEntry[], x: number, y: number): void {
    this.close();
    const root = document.createElement('div');
    root.className = 'player-menu';
    root.setAttribute('role', 'menu');
    // The right click that opened it lands on the menu itself, not on the
    // canvas Phaser guards: the browser's own menu must not open over ours.
    root.addEventListener('contextmenu', (event) => {
      event.preventDefault();
    });
    const heading = document.createElement('div');
    heading.className = 'player-menu__title';
    heading.textContent = uiText(title);
    root.append(heading);
    for (const entry of entries) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'player-menu__entry';
      button.setAttribute('role', 'menuitem');
      button.textContent = uiText(entry.label);
      button.addEventListener('click', () => {
        this.close();
        entry.run();
      });
      root.append(button);
    }
    // Kept on screen: a click near the right or bottom edge opens it inwards.
    // Measured once in the page, since its height depends on the entries and
    // on the device (44 px rows under a finger, party.css).
    root.style.left = `${String(x)}px`;
    root.style.top = `${String(y)}px`;
    document.body.append(root);
    const box = root.getBoundingClientRect();
    const margin = 8;
    const left = Math.max(margin, Math.min(x, window.innerWidth - box.width - margin));
    const top = Math.max(margin, Math.min(y, window.innerHeight - box.height - margin));
    root.style.left = `${String(Math.round(left))}px`;
    root.style.top = `${String(Math.round(top))}px`;
    this.root = root;
    window.addEventListener('keydown', this.onKey);
    // Next frame: the click that opened the menu must not close it.
    window.setTimeout(() => {
      window.addEventListener('pointerdown', this.onOutside, true);
    }, 0);
  }

  close(): void {
    window.removeEventListener('keydown', this.onKey);
    window.removeEventListener('pointerdown', this.onOutside, true);
    this.root?.remove();
    this.root = undefined;
  }
}
